import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { glossary } from '@sorare/core/src/lib/glossary';
import { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import { useGetTokenSingleSaleDetails } from '#src/hooks/offers/useGetTokenSingleSaleDetails.ts';
import { useIsPrimaryBuyLive } from '#src/hooks/primaryBuy/useIsPrimaryBuyLive.ts';

import type { BuyingFloorPricePlayerDetails_anyPlayer } from './__generated__/index.graphql.ts';

const Row = styled(Horizontal).attrs({ gap: 0.5 })`
  width: 100%;
`;
const ExponentRow = styled(Row)`
  justify-content: flex-end;
`;

type Props = {
  anyPlayer: BuyingFloorPricePlayerDetails_anyPlayer;
  inSeason: boolean;
};

export const BuyingFloorPricePlayerDetails = ({
  anyPlayer,
  inSeason,
}: Props) => {
  const seasonality = inSeason
    ? So5LeaderboardSeasonality.IN_SEASON
    : So5LeaderboardSeasonality.ALL_SEASONS;
  const { lowestPriceAnyCard } = anyPlayer;
  const offer = useGetTokenSingleSaleDetails(lowestPriceAnyCard);
  const primaryBuy = useIsPrimaryBuyLive(lowestPriceAnyCard);
  const monetaryAmount = (primaryBuy || offer)?.price;

  const { main, exponent } = useAmountWithConversion({
    monetaryAmount: monetaryAmount || MonetaryAmount.zero,
  });

  if (!monetaryAmount) return null;

  return (
    <Vertical>
      <LabelS as="p" color="var(--c-nd-600)">
        <FormattedMessage {...glossary.lowestPrice} />
      </LabelS>
      <Vertical gap={0}>
        <Row>
          <SportSeasonalityIcon seasonality={seasonality} />
          <BodyM bold as="span" color="var(--c-white)">
            {main}
          </BodyM>
        </Row>
        {!!exponent && (
          <ExponentRow>
            <BodyS as="span" color="var(--c-nd-600)">
              {exponent}
            </BodyS>
          </ExponentRow>
        )}
      </Vertical>
    </Vertical>
  );
};

BuyingFloorPricePlayerDetails.fragments = {
  anyPlayer: gql`
    fragment BuyingFloorPricePlayerDetails_anyPlayer on AnyPlayerInterface {
      slug
      lowestPriceAnyCard(inSeason: $inSeason, rarity: $rarity)
        @include(if: $withBuyingOptions) {
        slug
        ...useGetTokenSingleSaleDetails_anyCard
        ...useIsPrimaryBuyLive_anyCard
      }
    }
    ${useGetTokenSingleSaleDetails.fragments.anyCard}
    ${useIsPrimaryBuyLive.fragments.anyCard}
  ` as TypedDocumentNode<BuyingFloorPricePlayerDetails_anyPlayer>,
};
